import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export type UserRole =
  | "super_admin"
  | "admin"
  | "superadmin"
  | "Super Admin"
  | "survey_manager"
  | "Survey Manager"
  | "office_staff"
  | "Office Staff"
  | "field_engineer"
  | "Field Engineer"
  | "field_surveyor"
  | "Field Surveyor"
  | "lab_analyst"
  | "Lab Analyst"
  | "client_portal_user"
  | "Client Portal User"
  | string;

export interface ERPUser {
  name: string;
  role: UserRole;
  avatar: string;
  email?: string;
  avatarUrl?: string;
}

// Canonical role groups for easy permission checks
export const ROLE_GROUPS = {
  admins: ["super_admin", "admin", "superadmin", "Super Admin"],
  managers: ["super_admin", "admin", "superadmin", "Super Admin", "survey_manager", "Survey Manager"],
  officeAndAbove: ["super_admin", "admin", "superadmin", "Super Admin", "survey_manager", "Survey Manager", "office_staff", "Office Staff"],
  fieldStaff: ["field_engineer", "Field Engineer", "field_surveyor", "Field Surveyor"],
};

/**
 * useRoleGuard — reads the logged-in user from localStorage,
 * redirects to /login if not authenticated, redirects to /dashboard
 * if the user's role is NOT in the allowed list.
 *
 * @param allowedRoles  Array of roles permitted to view the page.
 *                      Pass an empty array [] to allow ALL authenticated users.
 * @param redirectTo    Where to send unauthorized users (default: /dashboard)
 */
export function useRoleGuard(
  allowedRoles: UserRole[] = [],
  redirectTo = "/dashboard"
): { user: ERPUser | null; loading: boolean; hasAccess: boolean } {
  const navigate = useNavigate();
  // Use a ref to hold allowedRoles so the effect doesn't re-run on every render
  // (arrays are new references each render, causing infinite loops)
  const allowedRolesRef = useRef(allowedRoles);
  allowedRolesRef.current = allowedRoles;

  const [user, setUser] = useState<ERPUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("erp_user");
    if (!stored) {
      navigate("/login", { replace: true });
      setLoading(false);
      return;
    }

    try {
      const parsed: ERPUser = JSON.parse(stored);
      const roles = allowedRolesRef.current;

      // Allow all roles if no restriction specified
      if (roles.length === 0) {
        setUser(parsed);
        setHasAccess(true);
        setLoading(false);
        return;
      }

      // Normalize role for comparison (trim whitespace, handle edge cases)
      const userRole = (parsed.role || "").trim();
      const allowed = roles.some(
        (r) => r.trim().toLowerCase() === userRole.toLowerCase()
      );

      if (allowed) {
        setUser(parsed);
        setHasAccess(true);
      } else {
        setHasAccess(false);
        navigate(redirectTo, { replace: true });
      }
    } catch {
      navigate("/login", { replace: true });
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, redirectTo]);

  return { user, loading, hasAccess };
}
